import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import './TablyPhoneMockup.scss';

const STEPS = [
  { id: 'scan',  label: 'Escanea el QR' },
  { id: 'order', label: 'Ordena' },
  { id: 'pay',   label: 'Paga' },
] as const;

const STEP_DURATION = 4500;
const ease = [0.21, 0.47, 0.32, 0.98] as const;

type StepId = (typeof STEPS)[number]['id'];

const TablyPhoneMockup: React.FC = () => {
  const reduced = useReducedMotion();
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => {
      setStepIdx((s) => (s + 1) % STEPS.length);
    }, STEP_DURATION);
    return () => clearInterval(id);
  }, [reduced]);

  const step: StepId = STEPS[stepIdx].id;

  return (
    <div className="tpm">
      <div className="tpm__glow" aria-hidden="true" />

      {/* Phone frame */}
      <div className="tpm__phone">
        <div className="tpm__screen">
          <div className="tpm__notch" />

          <div className="tpm__inner">
            <div className="tpm__statusbar">
              <span>9:41</span>
              <span>tably.mx</span>
            </div>

            <div className="tpm__stage">
              <AnimatePresence mode="wait">
                {step === 'scan'  && <ScanScreen  key="scan"  reduced={!!reduced} />}
                {step === 'order' && <OrderScreen key="order" reduced={!!reduced} />}
                {step === 'pay'   && <PayScreen   key="pay"   reduced={!!reduced} />}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Floating QR card */}
      <div className="tpm__qrcard">
        <div className="tpm__qrcard-inner">
          <QrPattern />
        </div>
        <p className="tpm__qrcard-label">Mesa 12</p>
      </div>

      <KitchenTicket stepIdx={stepIdx} />

      {/* Step indicator */}
      <div className="tpm__indicator">
        <div className="tpm__dots">
          {STEPS.map((s, i) => (
            <span
              key={s.id}
              className={`tpm__dot ${i === stepIdx ? 'tpm__dot--active' : ''}`}
              aria-hidden="true"
            />
          ))}
        </div>
        <div className="tpm__caption">
          <AnimatePresence mode="wait">
            <motion.p
              key={STEPS[stepIdx].id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="tpm__caption-text"
            >
              Paso {stepIdx + 1} · {STEPS[stepIdx].label}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const ScreenWrap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    className="tpm-screen"
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -14 }}
    transition={{ duration: 0.35, ease }}
  >
    {children}
  </motion.div>
);

/* ------------------------------- Step 1: Scan ------------------------------ */
const ScanScreen: React.FC<{ reduced: boolean }> = ({ reduced }) => (
  <ScreenWrap>
    <div className="tpm-scan__header">
      <p className="tpm-eyebrow">Cámara</p>
      <p className="tpm-scan__title">Apunta al código de la mesa</p>
    </div>

    <div className="tpm-scan__viewfinder">
      <span className="tpm-scan__bracket tpm-scan__bracket--tl" />
      <span className="tpm-scan__bracket tpm-scan__bracket--tr" />
      <span className="tpm-scan__bracket tpm-scan__bracket--br" />
      <span className="tpm-scan__bracket tpm-scan__bracket--bl" />
      <QrPattern />
      {!reduced && (
        <motion.div
          className="tpm-scan__line"
          initial={{ top: '10%' }}
          animate={{ top: ['10%', '90%', '10%'] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
    </div>

    <motion.div
      className="tpm-scan__chip"
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 1.6, duration: 0.4, ease }}
    >
      <span className="tpm-scan__check">✓</span>
      <p>Mesa 12 detectada</p>
    </motion.div>
  </ScreenWrap>
);

/* ------------------------------- Step 2: Order ----------------------------- */
const ITEMS = [
  { name: 'Tacos al pastor', sub: 'Orden de 3', price: 95 },
  { name: 'Agua de horchata', sub: '1 L', price: 45 },
  { name: 'Quesadillas', sub: '2 piezas', price: 70 },
];

const OrderScreen: React.FC<{ reduced: boolean }> = ({ reduced }) => {
  const [count, setCount] = useState(reduced ? ITEMS.length : 0);

  useEffect(() => {
    if (reduced) return;
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    ITEMS.forEach((_, i) => {
      timeouts.push(setTimeout(() => setCount(i + 1), 350 + i * 600));
    });
    return () => timeouts.forEach(clearTimeout);
  }, [reduced]);

  const subtotal = ITEMS.slice(0, count).reduce((acc, x) => acc + x.price, 0);

  return (
    <ScreenWrap>
      <div className="tpm-order__top">
        <div>
          <p className="tpm-eyebrow">Mesa</p>
          <p className="tpm-order__table">12</p>
        </div>
        <div className="tpm-order__guests">3 comensales</div>
      </div>

      <div className="tpm-order__items">
        {ITEMS.map((it, i) => (
          <motion.div
            key={it.name}
            className="tpm-order__item"
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={
              i < count
                ? { opacity: 1, y: 0 }
                : reduced
                  ? { opacity: 1, y: 0 }
                  : { opacity: 0, y: 12 }
            }
            transition={{ duration: 0.4, ease }}
          >
            <div className="tpm-order__item-left">
              <p className="tpm-order__item-name">{it.name}</p>
              <p className="tpm-order__item-sub">{it.sub}</p>
            </div>
            <p className="tpm-order__item-price">${it.price}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="tpm-order__subtotal"
        initial={reduced ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="tpm-order__subtotal-row">
          <span>Subtotal</span>
          <span>{count} artículo{count === 1 ? '' : 's'}</span>
        </div>
        <p className="tpm-order__subtotal-amount">${subtotal.toFixed(2)} MXN</p>
      </motion.div>
    </ScreenWrap>
  );
};

/* ------------------------------- Step 3: Pay ------------------------------- */
const PayScreen: React.FC<{ reduced: boolean }> = ({ reduced }) => {
  const [phase, setPhase] = useState<'ready' | 'processing' | 'done'>(
    reduced ? 'done' : 'ready'
  );

  useEffect(() => {
    if (reduced) return;
    const t1 = setTimeout(() => setPhase('processing'), 1100);
    const t2 = setTimeout(() => setPhase('done'), 2200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [reduced]);

  return (
    <ScreenWrap>
      <div>
        <p className="tpm-eyebrow">Mesa 12 · 3 comensales</p>
        <p className="tpm-pay__heading">Tu cuenta</p>
      </div>

      <div className="tpm-pay__total">
        <div className="tpm-pay__total-row">
          <span>Total</span>
          <span>3 artículos</span>
        </div>
        <p className="tpm-pay__total-amount">$210.00 MXN</p>
      </div>

      <div className="tpm-pay__method">
        <div>
          <p className="tpm-eyebrow">Método</p>
          <p className="tpm-pay__method-name">Visa terminada en 42</p>
        </div>
        <div className="tpm-pay__method-chip" />
      </div>

      <div className="tpm-pay__action">
        <AnimatePresence mode="wait">
          {phase === 'ready' && (
            <motion.button
              key="ready"
              type="button"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="tpm-pay__btn tpm-pay__btn--ready"
            >
              Pagar $210.00 MXN
            </motion.button>
          )}
          {phase === 'processing' && (
            <motion.div
              key="processing"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="tpm-pay__btn tpm-pay__btn--processing"
            >
              <motion.span
                className="tpm-pay__spinner"
                animate={{ rotate: 360 }}
                transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
              />
              Procesando…
            </motion.div>
          )}
          {phase === 'done' && (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease }}
              className="tpm-pay__btn tpm-pay__btn--done"
            >
              <span className="tpm-pay__check">✓</span>
              Pago confirmado
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </ScreenWrap>
  );
};

/* ---------------------------- Kitchen ticket ---------------------------- */
type TicketState = {
  header: string;
  label: string;
  note: string;
  variant: 'coral' | 'amber' | 'sage';
  delivered: boolean;
};

const TICKET_STATES: readonly TicketState[] = [
  { header: 'COCINA', label: 'Nueva orden', note: 'Recién recibida', variant: 'coral', delivered: false },
  { header: 'COCINA', label: 'Preparando',  note: 'En cocina · ~5 min', variant: 'amber', delivered: false },
  { header: 'MESERO', label: 'Entregada',   note: 'En la mesa', variant: 'sage', delivered: true },
];

const KitchenTicket: React.FC<{ stepIdx: number }> = ({ stepIdx }) => {
  const [payPhase, setPayPhase] = useState<'prep' | 'delivered'>('prep');

  useEffect(() => {
    if (stepIdx !== 2) return;
    setPayPhase('prep');
    const t = setTimeout(() => setPayPhase('delivered'), 2400);
    return () => clearTimeout(t);
  }, [stepIdx]);

  let state = TICKET_STATES[0];
  if (stepIdx === 2) {
    state = payPhase === 'prep' ? TICKET_STATES[1] : TICKET_STATES[2];
  }

  const items = ['3× Tacos pastor', '1L Horchata', '2× Quesadillas'];

  return (
    <AnimatePresence>
      {stepIdx !== 0 && (
        <motion.div
          key="kitchen-ticket"
          initial={{ opacity: 0, scale: 0.92, rotate: 0, y: 14 }}
          animate={{ opacity: 1, scale: 1, rotate: 6, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, rotate: 0, y: 14 }}
          transition={{ duration: 0.4, ease }}
          className="tpm__ticket"
        >
          <div className="tpm__ticket-header">
            <div className="tpm__ticket-header-text">
              <AnimatePresence mode="wait">
                <motion.p
                  key={state.header}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.25 }}
                  className="tpm__ticket-header-title"
                >
                  {state.header}
                </motion.p>
              </AnimatePresence>
              <p className="tpm__ticket-header-sub">Mesa 12 · 9:42</p>
            </div>
            <AnimatePresence mode="wait">
              <motion.span
                key={state.variant}
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.4, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className={`tpm__ticket-dot tpm__ticket-dot--${state.variant}`}
              />
            </AnimatePresence>
          </div>

          <ul className="tpm__ticket-items">
            {items.map((it) => (
              <li
                key={it}
                className={`tpm__ticket-item ${state.delivered ? 'tpm__ticket-item--delivered' : ''}`}
              >
                <span>{it}</span>
                <span className="tpm__ticket-item-mark">{state.delivered ? '✓' : '↻'}</span>
              </li>
            ))}
          </ul>

          <div className="tpm__ticket-footer">
            <AnimatePresence mode="wait">
              <motion.div
                key={state.label}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.3 }}
              >
                <p className={`tpm__ticket-label tpm__ticket-label--${state.variant}`}>
                  {state.label}
                </p>
                <p className="tpm__ticket-note">{state.note}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ----------------------------- QR pattern svg ----------------------------- */
const QrPattern: React.FC = () => (
  <svg viewBox="0 0 24 24" className="tpm-qr" fill="currentColor">
    <rect x="2" y="2" width="6" height="6" />
    <rect x="3" y="3" width="4" height="4" fill="#0E1116" />
    <rect x="4" y="4" width="2" height="2" />
    <rect x="16" y="2" width="6" height="6" />
    <rect x="17" y="3" width="4" height="4" fill="#0E1116" />
    <rect x="18" y="4" width="2" height="2" />
    <rect x="2" y="16" width="6" height="6" />
    <rect x="3" y="17" width="4" height="4" fill="#0E1116" />
    <rect x="4" y="18" width="2" height="2" />
    <rect x="10" y="3" width="2" height="2" />
    <rect x="13" y="2" width="1" height="3" />
    <rect x="11" y="6" width="2" height="2" />
    <rect x="14" y="7" width="2" height="2" />
    <rect x="10" y="10" width="2" height="2" />
    <rect x="13" y="11" width="3" height="2" />
    <rect x="17" y="11" width="2" height="2" />
    <rect x="20" y="10" width="2" height="2" />
    <rect x="11" y="14" width="2" height="2" />
    <rect x="14" y="14" width="2" height="3" />
    <rect x="18" y="15" width="2" height="2" />
    <rect x="20" y="17" width="2" height="2" />
    <rect x="10" y="18" width="2" height="2" />
    <rect x="13" y="19" width="2" height="2" />
    <rect x="16" y="20" width="3" height="2" />
  </svg>
);

export default TablyPhoneMockup;
